import React, { useState, useMemo, useEffect } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { Redirect } from 'react-router-dom'
import { Body, FlexWrapper } from 'components/Shell/Shell'
import { Spinner } from 'components/Loaders/Loaders'
import ICONS from 'components/Icons/Icons'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
import '../Forms/Forms.scss'
import SettingsListView from './SettingsViews/SettingsListView'
import { FilingToRecordTypes } from './Notary/FilingToRecordTypes'
import UCCFilingToRecordTypeXref from './UCC/UCCFilingToRecordTypeXref'
import RecordDetails from './RecordDetails/RecordDetails'
import * as api from '../../requests'
import ReceiptItemToFilingType from './Fiscal/ReceiptItemToFilingType'
import BusinessFilingToRecordTypeXref from './Business/BusinessFilingToRecordTypeXref'
import OrderFilingTypeXref from './Business/OrderFilingTypeXref'
import BusinessRecordTypeSubTypeXref from './Business/BusinessRecordTypeSubTypeXref'
import BusinessConversionsXref from './Business/BusinessConversionsXref'
import BusinessMergersXref from './Business/BusinessMergersXref'
import TrademarkFilingToRecordTypeXref from './Trademark/TrademarkFilingToRecordTypeXref'
import SettingImportExport from './General/SettingImportExport'
import { setErrorToast } from '../../actions'
import RegisteredAgentFilingTypeXref from './RegisteredAgent/RegisteredAgentFilingTypeXref'
import AthleteAgentFilingTypeXref from './AthleteAgent/AthleteAgentFilingTypeXref'
import { FilingSourceTypeIds } from '../../Utilities/Utilities'
import CharityFilingTypeXref from './Charity/CharityFilingTypeXref'
import ContractorFilingTypeXref from './Contractor/ContractorFilingTypeXref'
import LobbyistFilingTypeXref from './Lobbyist/LobbyistFilingTypeXref'
import MiscRecordsFieldDefinitionSettings from './MiscRecords/MiscRecordsFieldDefinitionSettings'
import MiscRecordsFilingTypeSettings from './MiscRecords/MiscRecordsFilingTypeSettings'

const Settings = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const { appSettings, activeSourceTypes } = useSelector((state) => state.app, shallowEqual)
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [pages, setPages] = useState([])
  const [settingCategories, setSettingCategories] = useState([])

  useEffect(() => {
    api.GET_SETTING_PAGES_CONFIG(
      (pages) => {
        setLoading(false)
        setPages(pages)
        const categories = []
        pages
          .sort((a, b) => (a.Category < b.Category ? 1 : -1))
          .sort((a, b) => (a.TabName > b.TabName ? 1 : -1))
          .some((page) => {
            if (
              page.Redirect === 'wordManagement' &&
              !activeSourceTypes.find(
                (sourceType) =>
                  (sourceType.SourceTypeId === FilingSourceTypeIds.Business ||
                    sourceType.SourceTypeId === FilingSourceTypeIds.Lien) &&
                  sourceType.IsActive
              )
            )
              return null

            if (
              page.IsActive &&
              (page.RequiredRightNames.length === 0 ||
                page.RequiredRightNames.some((rightName) => appUserRights.includes(rightName)))
            ) {
              const foundCategory = categories.find(
                (c) => c.CategoryName === page.Category && c.TabName === page.TabName
              )
              if (!foundCategory) {
                categories.push({
                  CategoryName: page.Category,
                  TabName: page.TabName,
                  TabParameterPath: page.TabParameterPath,
                  Pages: [page],
                })
              } else {
                foundCategory.Pages.push(page)
              }
            }
            return null
          })
        setSettingCategories(categories)
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to load Setting Pages'))
        setLoading(false)
      }
    )
  }, [activeSourceTypes])

  const pathNameParts = useMemo(() => {
    return location.pathname.split('/')
  }, [location.pathname])

  const selectedTab = useMemo(() => {
    if (pathNameParts.length > 2) {
      const categoryIndex = pathNameParts.indexOf('settings') + 1
      return pathNameParts[categoryIndex]
    }
    return ''
  }, [pathNameParts])

  const pageRedirect = useMemo(() => {
    if (pathNameParts.length > 3) {
      const settingIndex = pathNameParts.indexOf('settings') + 2
      return pathNameParts[settingIndex]
    }
    return null
  }, [pathNameParts])

  useEffect(() => {
    if (selectedTab === '') history.replace('/settings/all')
    setFilter('')
  }, [selectedTab])

  const settingsComponent = useMemo(() => {
    if (!pageRedirect) return null
    switch (pageRedirect) {
      case 'businessConversions':
        return <BusinessConversionsXref />
      case 'businessFilingToRecordTypes':
        return <BusinessFilingToRecordTypeXref />
      case 'businessMergers':
        return <BusinessMergersXref />
      case 'businessSubTypeToRecordTypes':
        return <BusinessRecordTypeSubTypeXref />
      case 'charityFilingTypeXref':
        return <CharityFilingTypeXref />
      case 'contractorFilingTypeXref':
        return <ContractorFilingTypeXref />
      case 'filingToRecordTypes': // Notary
        return <FilingToRecordTypes />
      case 'lobbyistFilingTypeXref':
        return <LobbyistFilingTypeXref />
      case 'orderFilingTypeXref':
        return <OrderFilingTypeXref />
      case 'receiptItemToFilingTypes':
        return <ReceiptItemToFilingType />
      case 'recordDetailConfig':
        return <RecordDetails />
      case 'trademarkFilingTypeXref':
        return <TrademarkFilingToRecordTypeXref />
      case 'uccFilingToRecordTypes':
        return <UCCFilingToRecordTypeXref />
      case 'settingImportExport':
        return <SettingImportExport pages={pages} />
      case 'registeredAgentFilingToRecordTypes':
        return <RegisteredAgentFilingTypeXref />
      case 'athleteAgentFilingToRecordTypes':
        return <AthleteAgentFilingTypeXref />
      case 'miscFieldDefinitions':
        return <MiscRecordsFieldDefinitionSettings />
      case 'miscFilingTypes':
        return <MiscRecordsFilingTypeSettings />
      default:
        if (loading) return <Spinner className="app-page-loader" themeColor="dark-neutral" />
        const page = pages.find((page) => page.Redirect === pageRedirect)
        if (!page) return 'Selected Setting Page NOT configured'
        const isReadOnly = pageRedirect === 'miscellaneousModules' && !appUserRights.includes('CanConfigureMiscellaneousModules')
        return <SettingsListView {...page} readOnly={isReadOnly} />
    }
  }, [pageRedirect, pages, loading, appUserRights])

  return (
    <Body className={pageRedirect ? 'settings-blue-gray' : 'forms'} hiddenTitle="Settings">
      {appSettings.WEBSITE_PORTAL_OFFLINE_YN === true && <Redirect to="/" />}
      {settingsComponent ? (
        settingsComponent
      ) : (
        <React.Fragment>
          <Toolbar filter={filter} onChange={setFilter} />
          <FlexWrapper direction="column" aria-busy={loading}>
            {loading ? (
              <div className="loading-wrapper forms-wrapper">
                <Spinner className="page-loader" themeColor="neutral" />
              </div>
            ) : (
              settingCategories
                .filter((tc) => {
                  if (selectedTab === 'all') return true
                  return tc.TabParameterPath.toLowerCase() === selectedTab.toLowerCase()
                })
                .map((tc) => {
                  return (
                    <SettingsCategory
                      filter={filter}
                      selectedTab={selectedTab}
                      key={`${tc.TabName}-${tc.CategoryName}`}
                      {...tc}
                    />
                  )
                })
            )}
          </FlexWrapper>
        </React.Fragment>
      )}
    </Body>
  )
}
export default Settings

const Toolbar = ({ onChange, filter }) => {
  return (
    <div className="toolbar">
      <div className="filter-wrapper">
        <input
          type="text"
          className="filter"
          placeholder="Filter"
          value={filter}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Filter forms"
        />
        {filter && (
          <button aria-label="clear filter" className="clear" onClick={() => onChange('')}>
            <CrossIcon />
          </button>
        )}
      </div>
    </div>
  )
}

const SettingsCategory = ({ filter, selectedTab, Pages, CategoryName, TabName }) => {
  const displayTitle = useMemo(() => {
    if (selectedTab === 'all') return `${TabName} - ${CategoryName}`
    return CategoryName
  }, [selectedTab, TabName, CategoryName])

  const filteredPages = useMemo(() => {
    if (displayTitle.toLowerCase().includes(filter.toLowerCase())) return Pages
    return (Pages || []).filter((page) => page.Name.toLowerCase().includes(filter.toLowerCase()))
  }, [filter, Pages, displayTitle])

  return (
    filteredPages.length > 0 && (
      <div className="category">
        <div className="title-wrapper">
          <h2 className="title">{displayTitle}</h2>
        </div>
        <div className="form-blocks">
          {filteredPages.map((page) => {
            return (
              page && (
                <SettingBlock
                  label={page.Name}
                  icon={page.Icon}
                  redirect={`/settings/${page.TabParameterPath}/${page.Redirect}`}
                  key={page.Name}
                />
              )
            )
          })}
        </div>
      </div>
    )
  )
}

const SettingBlock = ({ label, icon, redirect, children }) => {
  const history = useHistory()
  return (
    <div
      role="button"
      className={`form-block-wrapper`}
      onClick={() => {
        history.push(redirect)
      }}
    >
      <div className="form-block">
        <SettingIcon icon={icon} />
        <h3>
          <span className="title">{label}</span>
        </h3>
        {children}
      </div>
    </div>
  )
}

const SettingIcon = ({ style = {}, className = '', icon }) => {
  return (
    <div className={`form-block-icon-container ${className}`} style={style}>
      <div className="abbr-wrapper">
        <div className="abbr">{ICONS[icon]}</div>
        {ICONS['gears']}
      </div>
    </div>
  )
}

